import gardenRoomHero from "@/assets/garden-room.png";

export interface Room {
  slug: string;
  name: string;
  tagline: string;
  beds: string;
  ensuite: boolean;
  size: string;
  description: string;
  longDescription: string;
  features: string[];
  heroImage: string;
  images: string[];
}

export const rooms: Room[] = [
  {
    slug: "main",
    name: "Main Bedroom",
    tagline: "King-size Bed \u00b7 En-suite",
    beds: "1 King-size bed",
    ensuite: true,
    size: "22 sqm",
    description: "The heart of the farmhouse. Wake up to golden Free State light and wide open views.",
    longDescription: "The main bedroom is the crown jewel of Rietkuil Farmhouse. A generous king-size bed dressed in crisp linen, an en-suite bathroom with both bath and shower, and large windows that frame the sunrise over the veld. Soft morning light pours in through sheer curtains, and the gentle rustle of the farm wakes you naturally. The walk-in closet gives you space to settle in, while the private garden access means your first step each morning is onto dew-kissed grass.",
    features: [
      "King-size bed with premium linen",
      "En-suite bathroom (bath \u0026 shower)",
      "Walk-in closet",
      "Garden access",
      "Garden and veld views",
      "Air conditioning",
      "Blackout curtains",
      "Bedside reading lamps",
    ],
    heroImage: "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/54e10937e828e2ba93027c918592da6e.png",
    images: [],
  },
  {
    slug: "bedroom-two",
    name: "Bedroom Two",
    tagline: "Double Bed",
    beds: "1 Double bed",
    ensuite: false,
    size: "16 sqm",
    description: "A warm and restful double room with soft natural light and peaceful farm views.",
    longDescription: "Bedroom Two offers a cozy retreat with a comfortable double bed and a calming view of the farmstead. The room faces west, catching the golden afternoon light that turns the walls honey-coloured. Antique bedside tables and a woven rug underfoot add texture and warmth. Guests share the nearby bathroom, which is just a few steps down the hallway and features both a bath and shower.",
    features: [
      "Double bed with quality linen",
      "West-facing afternoon light",
      "Farmstead views",
      "Antique bedside furniture",
      "Shared bathroom nearby",
      "Ceiling fan",
      "Woven area rug",
      "Bedside reading lamps",
    ],
    heroImage: "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/7331c66536b30aa6da19c32642f7822a.png",
    images: [
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/3f070f080ff54f63f0162faf343cc562.png",
    ],
  },
  {
    slug: "bedroom-three",
    name: "Bedroom Three",
    tagline: "Twin Beds",
    beds: "2 Single beds",
    ensuite: false,
    size: "15 sqm",
    description: "A charming twin room with two comfortable single beds and a calm, airy atmosphere.",
    longDescription: "Bedroom Three is perfect for children or friends travelling together. Two sturdy wooden single beds are dressed in crisp white linen, and the soft blue-and-cream colour palette gives the room a fresh, airy feel. A small writing desk sits beneath the window, ideal for morning journaling or sketching the birds outside. The room shares the family bathroom just down the corridor.",
    features: [
      "2 Single wooden beds",
      "Crisp white linen",
      "Writing desk by the window",
      "Soft blue \u0026 cream palette",
      "Bird-watching from the window",
      "Shared bathroom nearby",
      "Ceiling fan",
      "Bedside reading lamps",
    ],
    heroImage: "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/cbb9afb134ad8ed3b11109a2585ad810.png",
    images: [
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/db295ba16c5b1b54dbbc90b4919dabdd.png",
    ],
  },
  {
    slug: "bedroom-four",
    name: "Bedroom Four",
    tagline: "Twin Beds",
    beds: "2 Single beds",
    ensuite: false,
    size: "14 sqm",
    description: "A bright twin room with whitewashed walls, wooden floors, and a view of the orchard.",
    longDescription: "Bedroom Four exudes a calm farmhouse simplicity. Whitewashed walls reflect the morning sun, while polished wooden floors feel cool underfoot even on the warmest Free State days. The two single beds are positioned to catch cross-breezes from the orchard-facing windows. It is a favourite with families — children love falling asleep to the hoot of the nearby barn owl.",
    features: [
      "2 Single beds with fresh linen",
      "Whitewashed walls",
      "Polished wooden floors",
      "Orchard-facing windows",
      "Natural cross-ventilation",
      "Shared bathroom nearby",
      "Ceiling fan",
      "Bedside reading lamps",
    ],
    heroImage: "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/fd37c4dd645a84632e462dfec22beca0.png",
    images: [
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/69afefdd64ec83ec8bf73f28629896e7.png",
    ],
  },
  {
    slug: "garden-room",
    name: "Garden room",
    tagline: "Private \u00b7 Separate Entrance",
    beds: "1 Double bed",
    ensuite: true,
    size: "30 sqm",
    description: "A self-contained garden room with its own entrance, kitchenette, and en-suite \u2014 total privacy.",
    longDescription: "The garden room is perfect for couples who want a little more independence, or for in-laws who prefer their own space. With a private entrance off the stoep, a compact but fully equipped kitchenette, and an en-suite shower room, it is a complete mini-home within the farm. The double bed faces a small private patio where morning coffee tastes different \u2014 quieter, somehow.",
    features: [
      "Double bed with quality linen",
      "En-suite shower room",
      "Private entrance via stoep",
      "Compact kitchenette",
      "Private patio with seating",
      "Self-contained mini-home",
      "Air conditioning",
      "Mini fridge & kettle",
    ],
    heroImage: gardenRoomHero,
    images: [
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/5adf3edd2c26b73c8fc51a97d840ccd8.png",
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/5a4f969c5ae61921781edf6131639710.png",
    ],
  },
  {
    slug: "bathrooms",
    name: "Bathrooms",
    tagline: "1 bathroom, separate toilet",
    beds: "N/A",
    ensuite: false,
    size: "Shared",
    description: "Clean, spacious bathrooms with both bath and shower options across the farmhouse.",
    longDescription: "Rietkuil offers between two and three bathrooms depending on your group configuration, so no one ever queues for a shower. The main bathroom features a full-size bath beneath a wide window \u2014 nothing beats a long soak after a day exploring the farm. A second bathroom offers a walk-in shower with strong water pressure (solar-heated, naturally). All are stocked with soft towels, basic toiletries, and that fresh, clean smell that only a well-kept country home achieves.",
    features: [
      "Full-size bath (main bathroom)",
      "Walk-in shower (second bathroom)",
      "Solar-heated water",
      "Soft towels provided",
      "Basic toiletries included",
      "Cleaned before every stay",
      "Wide windows with farm views",
      "Wooden vanity \u0026 storage",
    ],
    heroImage: "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/7942ffcfae1533c98379a0d1f01dcb91.png",
    images: [
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/bf5cd00e8c388f514d5a699ffe391886.png",
      "https://static.readdy.ai/image/479b996d3c1e6a9f41ba8a6f9c49d20b/149778b80d614f0d8ba4142e5744e32b.png",
    ],
  },
];